import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import logo from "../Assets/images/logo.png";

const NewsCard = ({
  image,
  category,
  state,
  title,
  author,
  reporter_image,
  date,
  readTime,
  fullNews,
  videoUrl,
  link,
}) => {
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [speaking, setSpeaking] = useState(false);

  const synthRef = useRef(window.speechSynthesis);

  useEffect(() => {
    const synth = synthRef.current;

    const loadVoices = () => {
      synth.getVoices();
    };

    loadVoices();

    if (synth.onvoiceschanged !== undefined) {
      synth.onvoiceschanged = loadVoices;
    }

    return () => {
      synth.cancel();
    };
  }, []);

  useEffect(() => {
    return () => {
      window.speechSynthesis.cancel();
    };
  }, []);

  const cleanText = (text) => {
    if (!text) return "";

    return text
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  };

  const displayCategory = state ? state : (category && category.trim() ? category : "सामान्य");

  // =========================
  // SHARE FUNCTION
  // =========================
 const handleShare = async (e) => {
  e.stopPropagation();

  try {
    const shareUrl = new URL(link, window.location.origin).href;

    if (navigator.share) {
      await navigator.share({
        title: title || "News",
        text: title || "Check this news",
        url: shareUrl,
      });
    } else {
      await navigator.clipboard.writeText(shareUrl);
      alert("Link copied successfully!");
    }
  } catch (error) {
    console.log("Share failed:", error);
  }
};

  // =========================
  // TEXT TO SPEECH
  // =========================
  const handleListen = (e) => {
    e.stopPropagation();

    const synth = synthRef.current;

    if (speaking) {
      synth.cancel();
      setSpeaking(false);
      return;
    }

    synth.cancel();

    const cleanedText = cleanText(fullNews);

    const voices = synth.getVoices();

    const hindiVoice =
      voices.find((v) => v.lang === "hi-IN") ||
      voices.find((v) => v.lang.includes("hi"));

    const chunks = cleanedText?.match(/.{1,180}/g);

    let index = 0;

    const speakChunk = () => {
      if (!chunks || index >= chunks.length) {
        setSpeaking(false);
        return;
      }

      const utterance = new SpeechSynthesisUtterance(chunks[index]);

      if (hindiVoice) {
        utterance.voice = hindiVoice;
      } else {
        utterance.lang = "hi-IN";
      }

      utterance.rate = 0.85;
      utterance.pitch = 1;

      utterance.onend = () => {
        index++;
        speakChunk();
      };

      utterance.onerror = () => {
        setSpeaking(false);
      };

      synth.speak(utterance);
    };

    speakChunk();
    setSpeaking(true);
  };

  return (
    <div
      onClick={() => navigate(link)}
      className="bg-white p-1 border border-gray-200 rounded-md overflow-hidden shadow-sm hover:shadow-md transition cursor-pointer"
    >
      {/* IMAGE */}
      <div className="relative aspect-[16/9] overflow-hidden">
        <img
          src={image}
          alt={title}
          className="w-full h-full object-contain"
        />

        {/* VIDEO PLAY BUTTON */}
        {videoUrl && (
          <a
            href={videoUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="absolute inset-0 flex items-center justify-center"
          >
            <div className="w-14 h-14 rounded-full bg-red-600/90 hover:bg-red-700 flex items-center justify-center shadow-lg transition">
              <i className="fa-solid fa-play text-white text-xl ml-1"></i>
            </div>
          </a>
        )}

        {/* LOGO */}
        <div className="absolute top-1 right-1 w-11 h-11">
          <img
            src={logo}
            alt="logo"
            className="w-10 h-10 object-contain"
          />
        </div>

        {/* CATEGORY */}
        <div className="absolute bottom-3 left-3 bg-red-600 text-white text-xs px-3 py-1 rounded-full">
          {displayCategory}
        </div>
      </div>

      {/* CONTENT */}
      <div className="relative p-2 overflow-hidden">
        {/* TITLE */}
        <h3 className="font-bold text-[18px] leading-snug text-gray-900 line-clamp-3 hover:text-red-600 transition">
          {title}
        </h3>

        {/* AUTHOR */}
        <div className="mt-3 text-xs text-gray-500 space-y-1">
          <div className="flex items-center gap-2">
            {reporter_image ? (
              <img
                src={reporter_image}
                alt={author || "Reporter"}
                className="w-6 h-6 rounded-full object-cover"
              />
            ) : (
              <i className="fa-regular fa-user"></i>
            )}

            <span>{author || "Unknown"}</span>
          </div>

          <div className="flex items-center gap-1">
            <i className="fa-regular fa-clock"></i>

            <span>
              {date} • {readTime}
            </span>
          </div>
        </div>

        {/* BUTTONS */}
        <div className="mt-3 flex items-center justify-between">
          {/* READ MORE */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setOpen(true);
            }}
            className="inline-flex items-center gap-2 text-sm font-semibold text-red-600 hover:text-red-700"
          >
            Read More

            <i className="fa-solid fa-arrow-up-right-from-square text-xs"></i>
          </button>

          {/* SHARE BUTTON */}
          <button
            onClick={handleShare}
            className="w-9 h-9 rounded-full border border-gray-300 flex items-center justify-center hover:bg-red-50 hover:border-red-500 transition"
          >
            <i className="fa-solid fa-share-nodes text-red-600"></i>
          </button>
        </div>

        {/* FULL NEWS SLIDE PANEL */}
        <div
          onClick={(e) => e.stopPropagation()}
          className={`absolute inset-0 bg-white z-40 transition-transform duration-300 ease-in-out
          ${open ? "translate-y-0" : "translate-y-full"}`}
        >
          <div className="p-4 h-full flex flex-col">
            {/* NEWS CONTENT */}
            <div className="text-sm text-gray-700 overflow-y-auto flex-1 leading-relaxed">
              {fullNews}
            </div>

            {/* ACTION BUTTONS */}
            <div className="mt-4 flex gap-3 flex-wrap">
              {/* LISTEN */}
              <button
                onClick={handleListen}
                className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded text-sm hover:bg-red-700 transition"
              >
                <i
                  className={`fa-solid ${
                    speaking ? "fa-stop" : "fa-volume-high"
                  }`}
                ></i>

                {speaking ? "Stop" : "Listen"}
              </button>

              {/* SHARE */}
              <button
                onClick={handleShare}
                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 transition"
              >
                <i className="fa-solid fa-share-nodes"></i>
                Share
              </button>

              {/* CLOSE */}
              <button
                onClick={() => {
                  setOpen(false);
                  synthRef.current.cancel();
                  setSpeaking(false);
                }}
                className="flex items-center gap-2 border border-gray-300 px-4 py-2 rounded text-sm hover:bg-gray-100 transition"
              >
                <i className="fa-solid fa-xmark"></i>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsCard;